import React, { useMemo, useState } from 'react'
import { SearchX, Send, Users } from 'lucide-react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { Button, Checkbox, Input, Skeleton } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { useTableSettings } from '@/hooks/TableSettings/useTableSettings'
import { renderSortIcon } from '@/hooks/TableSettings/generalTable.utils'
import { sortPeopleModels } from '@/hooks/TableSettings/peopleTable.utils'
import { buildPeopleRows } from '../cohortV2Data'
import { PersonRow } from './PersonRow'
import { SubmissionResultsDialog, type ResultsDetails } from '../assignments/SubmissionResultsDialog'

interface PeopleTabProps {
  cohortId: string
  // Manually-added cohort members; identity is joined from the directory client-side.
  memberPersonIds: string[]
  assignmentsDetails: PartnerService.AssignmentDetails[]
  directoryById: Map<string, UserService.PersonDirectoryEntry>
  // The directory may still be loading while board data renders — rows show progressively.
  directoryLoading: boolean
  // Resolves an assignmentId to its task's display name (from the board's taskNames map).
  getAssignmentTaskName: (assignmentId: string) => string
  isLoading: boolean
  checkedPersonIds: Set<string>
  setCheckedPersonIds: React.Dispatch<React.SetStateAction<Set<string>>>
  onSendToSelected: () => void
}

export const PeopleTab: React.FC<PeopleTabProps> = ({
  cohortId, memberPersonIds, assignmentsDetails, directoryById, directoryLoading,
  getAssignmentTaskName, isLoading, checkedPersonIds, setCheckedPersonIds, onSendToSelected,
}) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const [search, setSearch] = useState('')
  const [resultsOpen, setResultsOpen] = useState(false)
  const [results, setResults] = useState<ResultsDetails | null>(null)
  const onViewResults = (d: ResultsDetails) => { setResults(d); setResultsOpen(true) }

  const { sortColumn, sortDirection, toggleSort, getSortState } = useTableSettings({
    tableId: `cohortPeopleV2-${cohortId}`,
  })

  // Identity (name/email/onboardingStatus) and the real joined date are joined from the directory;
  // counts/grade come from the board's assignment details. No per-member fetch.
  const models = useMemo(
    () => buildPeopleRows(memberPersonIds, assignmentsDetails, directoryById),
    [memberPersonIds, assignmentsDetails, directoryById],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return models
    return models.filter((m) => {
      const name = `${m.person?.firstName || ''} ${m.person?.lastName || ''}`.toLowerCase()
      return name.includes(q) || (m.person?.email || '').toLowerCase().includes(q) || m.personId.toLowerCase().includes(q)
    })
  }, [models, search])

  const sorted = useMemo(() => sortPeopleModels(filtered, sortColumn, sortDirection), [filtered, sortColumn, sortDirection])

  const filteredIds = useMemo(() => filtered.map((m) => m.personId), [filtered])
  const allChecked = filteredIds.length > 0 && filteredIds.every((id) => checkedPersonIds.has(id))
  const someChecked = filteredIds.some((id) => checkedPersonIds.has(id))
  const toggleAll = () =>
    setCheckedPersonIds((prev) => {
      const next = new Set(prev)
      if (allChecked) filteredIds.forEach((id) => next.delete(id))
      else filteredIds.forEach((id) => next.add(id))
      return next
    })

  const toggle = (id: string, checked: boolean) =>
    setCheckedPersonIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id); else next.delete(id)
      return next
    })

  if (isLoading) {
    return <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[60px] w-full rounded-lg" />)}</div>
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Input placeholder={t('partner.searchPeople')} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
      </div>

      {checkedPersonIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm text-primary">
          <span>{t('partner.nSelected', { count: checkedPersonIds.size })}</span>
          <Button size="sm" className="ms-auto gap-2" onClick={onSendToSelected}>
            <Send className="h-4 w-4" />{t('partner.sendAssignment')}
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="grid h-9 grid-cols-[26px_auto_1fr_auto] items-center gap-3 border-b border-border bg-muted/20 px-3.5 text-[10px] uppercase tracking-wider text-muted-foreground lg:grid-cols-[26px_auto_minmax(0,1.5fr)_180px_minmax(0,1fr)]">
          <Checkbox
            aria-label={t('partner.selectAll')}
            checked={allChecked ? true : someChecked ? 'indeterminate' : false}
            onCheckedChange={toggleAll}
            disabled={filteredIds.length === 0}
            className="h-4 w-4"
          />
          <span />
          <span>
            <button onClick={() => toggleSort('name')} className="group flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors">
              <span>{t('common.name')}</span>{renderSortIcon(getSortState('name'))}
            </button>
          </span>
          <span className="hidden lg:block">
            <button onClick={() => toggleSort('status')} className="group flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors">
              <span>{t('partner.status')}</span>{renderSortIcon(getSortState('status'))}
            </button>
          </span>
          <span>
            <button onClick={() => toggleSort('assignments')} className="group flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors">
              <span>{t('partner.assignments')}</span>{renderSortIcon(getSortState('assignments'))}
            </button>
          </span>
        </div>
        {sorted.map((m) => (
          <PersonRow
            key={m.personId}
            model={m}
            directoryLoading={directoryLoading}
            checked={checkedPersonIds.has(m.personId)}
            onToggle={(c) => toggle(m.personId, c)}
            getAssignmentTaskName={getAssignmentTaskName}
            isTaskLoading={false}
            onViewResults={onViewResults}
          />
        ))}
        {sorted.length === 0 && (search
          ? <NoDataEmptyState icon={SearchX} title={t('common.noFilterResults')} description={t('common.noFilterResultsDescription')} />
          : <NoDataEmptyState icon={Users} title={tP('noPeopleYet')} description={tP('noPeopleDescription')} />
        )}
      </div>

      <SubmissionResultsDialog open={resultsOpen} onOpenChange={setResultsOpen} details={results} />
    </div>
  )
}
